import { localizedLandingSlugs } from "./locale-strategy";

export const wave1SeoLocales = ["pt", "es", "de", "fr", "ja"] as const;
export const chineseSeoLocales = ["zh", "zh-tw"] as const;
export const koreanSeoLocales = ["ko"] as const;
export const translatedSeoLocales = [
  ...wave1SeoLocales,
  ...chineseSeoLocales,
  ...koreanSeoLocales,
] as const;

export type Wave1SeoLocale = (typeof wave1SeoLocales)[number];
export type ChineseSeoLocale = (typeof chineseSeoLocales)[number];
export type KoreanSeoLocale = (typeof koreanSeoLocales)[number];
export type TranslatedSeoLocale = (typeof translatedSeoLocales)[number];
export type SeoLocaleReleaseStatus = "unavailable" | "preview" | "indexed";

export const localizedCorePaths = [
  "/",
  "/pricing",
  "/product-map",
  ...localizedLandingSlugs.map((slug) => `/${slug}` as const),
] as const;

export type LocalizedCorePath = (typeof localizedCorePaths)[number];

export const seoLocaleReleaseStatus: Record<
  TranslatedSeoLocale,
  SeoLocaleReleaseStatus
> = {
  pt: "preview",
  es: "preview",
  de: "preview",
  fr: "preview",
  ja: "preview",
  zh: "preview",
  "zh-tw": "preview",
  ko: "preview",
};

const languageTags: Record<string, string> = {
  en: "en-US",
  ja: "ja-JP",
  ko: "ko-KR",
  de: "de-DE",
  es: "es-ES",
  fr: "fr-FR",
  pl: "pl-PL",
  nl: "nl-NL",
  pt: "pt-BR",
  ru: "ru-RU",
  zh: "zh-CN",
  "zh-tw": "zh-TW",
};

const openGraphLocales: Record<string, string> = {
  en: "en_US",
  ja: "ja_JP",
  ko: "ko_KR",
  de: "de_DE",
  es: "es_ES",
  fr: "fr_FR",
  pl: "pl_PL",
  nl: "nl_NL",
  pt: "pt_BR",
  ru: "ru_RU",
  zh: "zh_CN",
  "zh-tw": "zh_TW",
};

export const isWave1SeoLocale = (
  locale: string,
): locale is Wave1SeoLocale =>
  wave1SeoLocales.includes(locale as Wave1SeoLocale);

export const isTranslatedSeoLocale = (
  locale: string,
): locale is TranslatedSeoLocale =>
  translatedSeoLocales.includes(locale as TranslatedSeoLocale);

export const isLocalizedCorePath = (
  path: string,
): path is LocalizedCorePath =>
  localizedCorePaths.includes(path as LocalizedCorePath);

export const isSeoPathTranslated = (locale: string, path: string) =>
  locale === "en" ||
  (isTranslatedSeoLocale(locale) &&
    seoLocaleReleaseStatus[locale] !== "unavailable" &&
    isLocalizedCorePath(path));

export const isSeoPathIndexable = (locale: string, path: string) =>
  locale === "en" ||
  (isTranslatedSeoLocale(locale) &&
    seoLocaleReleaseStatus[locale] === "indexed" &&
    isLocalizedCorePath(path));

export const getLanguageTag = (locale: string) =>
  languageTags[locale] || languageTags.en;

export const getOpenGraphLocale = (locale: string) =>
  openGraphLocales[locale] || openGraphLocales.en;

export const getLocalizedCanonical = (locale: string, path: string) =>
  locale === "en" ? path : `/${locale}${path === "/" ? "" : path}`;

export const getIndexedLocalesForPath = (path: string) => {
  if (!isLocalizedCorePath(path)) return ["en"] as string[];

  return [
    "en",
    ...translatedSeoLocales.filter(
      (locale) => seoLocaleReleaseStatus[locale] === "indexed",
    ),
  ];
};

export const getHreflangPaths = (path: string) => {
  const indexedLocales = getIndexedLocalesForPath(path);
  if (indexedLocales.length === 1) return undefined;

  return {
    ...Object.fromEntries(
      indexedLocales.map((locale) => [
        getLanguageTag(locale),
        getLocalizedCanonical(locale, path),
      ]),
    ),
    "x-default": path,
  };
};

export const getSeoAwarePath = (path: string, locale: string) =>
  isSeoPathTranslated(locale, path)
    ? getLocalizedCanonical(locale, path)
    : path;
